// #2732 stage 4: the settings screen's copy of the principal's AI work
// settings. The server (`src/lib/ai-work-settings.ts`) owns the defaults and
// sends them in `ai_work_view`; this file only edits and serializes.

export type AiWorkDoer = 'ai' | 'principal';

export interface AiWorkVerbView {
  how: string;
  doer: AiWorkDoer;
  default_doer: AiWorkDoer;
  content_check_default: boolean;
  content_check_fixed: boolean;
}

export interface AiWorkExceptionDraft {
  how: string;
  where: string | null;
  what: string | null;
  doer?: AiWorkDoer;
  content_check?: boolean;
}

export interface WorkHabitDraft {
  what: string;
  where: string;
}

/** #2762: a business task the principal added. */
export interface AiWorkCardDraft {
  what: string;
  steps: string[];
}

/** #2762: the principal's choice for one tool an AI declared. */
export interface AiWorkToolDraft {
  client_id: string;
  tool_name: string;
  doer: AiWorkDoer;
  content_check?: boolean;
}

export interface AiWorkView {
  source: 'ai_work' | 'default';
  verbs: AiWorkVerbView[];
  exceptions: AiWorkExceptionDraft[];
  habits: WorkHabitDraft[];
  cards?: AiWorkCardDraft[];
  tools?: AiWorkToolDraft[];
}

/** What the draft keeps: the doer per verb, exceptions, habits, added business tasks, tool choices. */
export interface AiWorkDraft {
  verbs: Record<string, AiWorkDoer>;
  exceptions: AiWorkExceptionDraft[];
  habits: WorkHabitDraft[];
  cards: AiWorkCardDraft[];
  tools: AiWorkToolDraft[];
}

export const EMPTY_AI_WORK_DRAFT: AiWorkDraft = { verbs: {}, exceptions: [], habits: [], cards: [], tools: [] };

/** Services offered as suggestions; any other service name can be typed. */
export const AI_WORK_SERVICE_SUGGESTIONS = [
  'gmail', 'slack', 'google_calendar', 'google_drive', 'notion', 'freee_accounting', 'freee_invoice',
  'freee_hr', 'outlook', 'outlook_calendar', 'teams', 'canva', 'browser',
] as const;

export function aiWorkDraftFromView(view: AiWorkView | null | undefined): AiWorkDraft {
  if (!view) return { ...EMPTY_AI_WORK_DRAFT };
  const verbs: Record<string, AiWorkDoer> = {};
  for (const row of view.verbs) verbs[row.how] = row.doer;
  return {
    verbs,
    exceptions: view.exceptions.map((row) => ({ ...row })),
    habits: view.habits.map((row) => ({ ...row })),
    cards: (view.cards ?? []).map((row) => ({ what: row.what, steps: [...row.steps] })),
    tools: (view.tools ?? []).map((row) => ({ ...row })),
  };
}

export function serializeAiWorkDraft(draft: AiWorkDraft): string {
  return JSON.stringify(draft);
}

export function parseAiWorkDraft(value: string): AiWorkDraft {
  try {
    const parsed = JSON.parse(value) as Partial<AiWorkDraft>;
    return {
      verbs: parsed.verbs && typeof parsed.verbs === 'object' ? parsed.verbs : {},
      exceptions: Array.isArray(parsed.exceptions) ? parsed.exceptions : [],
      habits: Array.isArray(parsed.habits) ? parsed.habits : [],
      cards: Array.isArray(parsed.cards) ? parsed.cards : [],
      tools: Array.isArray(parsed.tools) ? parsed.tools : [],
    };
  } catch {
    return { ...EMPTY_AI_WORK_DRAFT };
  }
}

/**
 * The value saved to `preferences.ai_work`: only verbs that differ from the
 * default, and only rows that say something. Blank service or object means
 * "any".
 */
export function aiWorkPreferenceFromDraft(draft: AiWorkDraft, verbViews: AiWorkVerbView[]) {
  const verbs: Record<string, AiWorkDoer> = {};
  for (const view of verbViews) {
    const doer = draft.verbs[view.how];
    if (doer && doer !== view.default_doer) verbs[view.how] = doer;
  }
  const exceptions = draft.exceptions
    .map((row) => ({
      how: row.how,
      where: row.where?.trim() || null,
      what: row.what?.trim() || null,
      ...(row.doer ? { doer: row.doer } : {}),
      ...(typeof row.content_check === 'boolean' ? { content_check: row.content_check } : {}),
    }))
    .filter((row) => row.how && (row.doer || typeof row.content_check === 'boolean'));
  const habits = draft.habits
    .map((row) => ({ what: row.what.trim(), where: row.where.trim() }))
    .filter((row) => row.what && row.where);
  const cards = draft.cards
    .map((row) => ({ what: row.what.trim(), steps: row.steps }))
    .filter((row) => row.what && row.steps.length > 0);
  const tools = draft.tools.map((row) => ({ ...row }));
  return { version: 1 as const, verbs, exceptions, habits, cards, tools };
}

/**
 * Once habits are saved they are the one place for services. The old profile
 * fields that named a service are cleared; who handles accounting stays.
 */
export function accountingWithoutSystems<T extends Record<string, unknown>>(accounting: T | null): T | null {
  if (!accounting) return null;
  const next: Record<string, unknown> = {};
  for (const [key, task] of Object.entries(accounting)) {
    if (task && typeof task === 'object' && !Array.isArray(task)) {
      const { system: _system, ...rest } = task as Record<string, unknown>;
      next[key] = rest;
    } else {
      next[key] = task;
    }
  }
  return next as T;
}
